import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { StatusBar } from 'react-native'

import moment from 'moment'

import { SafeAreaProvider } from 'react-native-safe-area-context'

import { withTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'

import UnlimitedContext from '../../Context'

import { BUNDLE_TYPE } from '../../Utils/bundle'
import { ROLE, STATUS } from '../../Utils/members'

import UnlimitedHomeJourney from '../../Components/UnlimitedHomeJourney'

const defaultState = {
  data: [],
  isError: false,
  isLoading: true
}

const getBundlePriceBody = ({
  customTVs,
  customSpeeds,
  customBundleOptions
}) => {
  const tvData = (customTVs.data ?? []).find(
    tv => tv.id === customBundleOptions.tv
  )
  const speedData = (customSpeeds.data ?? []).find(
    speed => speed.id === customBundleOptions.speed
  )
  return {
    tv: tvData?.name,
    speed: speedData?.name,
    plan: customBundleOptions.plan,
    slots: customBundleOptions.slots
  }
}

const JourneyScreen = props => {
  const { mapAPIKey, language, loggedInNumber, hasInstallation } = props
  // Bundle
  const [atlBundles, setAtlBundles] = useState({ ...defaultState })
  const [pegaBundles, setPegaBundles] = useState({ ...defaultState })
  const [bundleType, setBundleType] = useState(null)
  const [selectedBundle, setSelectedBundle] = useState(null)

  // Custom Bundle
  const [customTVs, setCustomTVs] = useState({ ...defaultState })
  const [customSlots, setCustomSlots] = useState({ ...defaultState })
  const [customPlans, setCustomPlans] = useState({ ...defaultState })
  const [customSpeeds, setCustomSpeeds] = useState({ ...defaultState })

  const [customBundlePrice, setCustomBundlePrice] = useState({
    price: null,
    discount: null,
    isError: false,
    isLoading: false
  })

  const [customBundleOptions, setCustomBundleOptions] = useState({
    tv: null,
    plan: null,
    slots: null,
    speed: null
  })

  // Location
  // hasCoverage [null, true, false]
  const [locationData, setLocationData] = useState({
    street: '',
    country: '',
    city: 'Tirana',
    latitude: '',
    longitude: '',
    isError: false,
    isLoading: false,
    hasCoverage: null
  })

  // Customer
  const [customerData, setCustomerData] = useState({
    fixId: null,
    isError: false,
    isLoading: false,
    isExisting: false,
    isCreating: false,
    isCreateError: false
  })

  // ID
  const [idData, setIdData] = useState({
    name: '',
    city: '',
    email: '',
    gender: '',
    selfie: '',
    backId: '',
    frontId: '',
    surname: '',
    birthday: '',
    backIdB64: '',
    selfieB64: '',
    frontIdB64: '',
    documentId: '',
    documentYear: '',
    documentMonth: '',
    documentCountry: ''
  })

  // Member
  const [groupData, setGroupData] = useState({
    name: '',
    members: []
  })

  // Installation
  const [installationData, setInstallationData] = useState({
    date: moment().add(1, 'day').format('YYYY-MM-DD'),
    time: ''
  })

  const context = useMemo(() => {
    return {
      data: {
        // General
        language,
        mapAPIKey,
        loggedInNumber,
        hasInstallation,
        // Bundle
        atlBundles,
        pegaBundles,
        bundleType,
        selectedBundle,
        // Custom Bundle
        customTVs,
        customSlots,
        customPlans,
        customSpeeds,
        customBundlePrice,
        customBundleOptions,
        // Location
        locationData,
        // Customer
        customerData,
        // ID
        idData,
        // Group
        groupData,
        // Installation
        installationData,
        installCalendar: props.installCalendar
      }
    }
  }, [
    // General
    language,
    mapAPIKey,
    loggedInNumber,
    hasInstallation,
    // Bundle
    atlBundles,
    pegaBundles,
    bundleType,
    selectedBundle,
    // Custom Bundle
    customTVs,
    customSlots,
    customPlans,
    customSpeeds,
    customBundlePrice,
    customBundleOptions,
    // Location
    locationData,
    // Customer
    customerData,
    // ID
    idData,
    // Group
    groupData,
    // Installation
    installationData,
    props.installCalendar
  ])

  const _getPegaBundles = ({ onStart, onSuccess, onError }) => {
    props.getPegaBundles({
      onStart: () => {
        setPegaBundles({ ...defaultState })
        onStart && onStart()
      },
      onSuccess: ({ offers }) => {
        setPegaBundles({ ...defaultState, isLoading: false, data: offers })
        onSuccess && onSuccess(offers)
      },
      onError: () => {
        setPegaBundles({ ...defaultState, isLoading: false, isError: true })
        onError && onError()
      }
    })
  }

  const _getATLBundles = ({ onStart, onSuccess, onError }) => {
    props.getATLBundles({
      onStart: () => {
        setAtlBundles({ ...defaultState })
        onStart && onStart()
      },
      onSuccess: ({ offers }) => {
        setAtlBundles({ ...defaultState, isLoading: false, data: offers })
        onSuccess && onSuccess(offers)
      },
      onError: () => {
        setAtlBundles({ ...defaultState, isLoading: false, isError: true })
        onError && onError()
      }
    })
  }

  const _validateLocation = ({ body, onStart, onSuccess, onError }) => {
    props.validateLocation({
      body,
      onStart: () => {
        setLocationData({
          ...body,
          isError: false,
          isLoading: true,
          hasCoverage: null
        })
        onStart && onStart()
      },
      onSuccess: ({ validationResult }) => {
        setLocationData(prev => {
          return {
            ...prev,
            isError: false,
            isLoading: false,
            hasCoverage: validationResult === 'success'
          }
        })
        onSuccess && onSuccess(validationResult)
      },
      onError: () => {
        setLocationData(prev => {
          return {
            ...prev,
            isError: true,
            isLoading: false,
            hasCoverage: null
          }
        })
        onError && onError()
      }
    })
  }

  const _getFixAndTVs = ({ onStart, onSuccess, onError }) => {
    props.getFixAndTVs({
      onStart: () => {
        setCustomTVs({ ...defaultState })
        setCustomSpeeds({ ...defaultState })
        onStart && onStart()
      },
      onSuccess: ({ tvs, speeds }) => {
        setCustomSpeeds({ ...defaultState, isLoading: false, data: speeds })
        setCustomTVs({ ...defaultState, isLoading: false, data: tvs })
        onSuccess && onSuccess({ tvs, speeds })
      },
      onError: () => {
        setCustomTVs({ ...defaultState, isLoading: false, isError: true })
        setCustomSpeeds({ ...defaultState, isLoading: false, isError: true })
        onError && onError()
      }
    })
  }

  const _getTariffPlans = ({ onStart, onSuccess, onError }) => {
    props.getTariffPlans({
      onStart: () => {
        setCustomPlans({ ...defaultState })
        onStart && onStart()
      },
      onSuccess: ({ plans }) => {
        setCustomPlans({ ...defaultState, isLoading: false, data: plans })
        onSuccess && onSuccess()
      },
      onError: () => {
        setCustomPlans({ ...defaultState, isLoading: false, isError: true })
        onError && onError()
      }
    })
  }

  const _getMemberSlots = ({ onStart, onSuccess, onError }) => {
    props.getMemberSlots({
      onStart: () => {
        setCustomSlots({ ...defaultState })
        onStart && onStart()
      },
      onSuccess: ({ slots }) => {
        setCustomSlots({ ...defaultState, isLoading: false, data: slots })
        onSuccess && onSuccess()
      },
      onError: () => {
        setCustomSlots({ ...defaultState, isLoading: false, isError: true })
        onError && onError()
      }
    })
  }

  const _getCustomBundlePrice = ({ onStart, onSuccess, onError }) => {
    props.getCustomBundlePrice({
      body: getBundlePriceBody({
        customTVs,
        customSpeeds,
        customBundleOptions
      }),
      onStart: () => {
        setCustomBundlePrice({
          price: null,
          discount: null,
          isError: false,
          isLoading: true
        })
        onStart && onStart()
      },
      onSuccess: ({ price, discount }) => {
        setCustomBundlePrice({
          price: price,
          isError: false,
          isLoading: false,
          discount: discount
        })
        onSuccess && onSuccess()
      },
      onError: () => {
        setCustomBundlePrice({
          price: null,
          discount: null,
          isError: true,
          isLoading: false
        })
        onError && onError()
      }
    })
  }

  const _checkCustomerByMSISDN = ({ onStart, onSuccess, onError }) => {
    props.checkCustomerByMSISDN({
      onStart: () => {
        setCustomerData({
          fixId: null,
          isError: false,
          isLoading: true,
          isExisting: false,
          isCreating: false,
          isCreateError: false
        })
        onStart && onStart()
      },
      onSuccess: ({ exists, fixId }) => {
        setCustomerData({
          fixId: fixId,
          isError: false,
          isLoading: false,
          isCreating: false,
          isExisting: exists,
          isCreateError: false
        })
        onSuccess && onSuccess()
      },
      onError: () => {
        setCustomerData({
          fixId: null,
          isError: true,
          isLoading: false,
          isCreating: false,
          isExisting: false,
          isCreateError: false
        })
        onError && onError()
      }
    })
  }

  const _createCustomer = ({ body, onStart, onSuccess, onError }) => {
    props.createCustomer({
      body,
      onStart: () => {
        setCustomerData(prev => {
          return {
            ...prev,
            isCreating: true,
            isCreateError: false
          }
        })
        onStart && onStart()
      },
      onSuccess: ({ id }) => {
        setCustomerData({
          fixId: id,
          isError: false,
          isExisting: true,
          isLoading: false
        })
        onSuccess && onSuccess()
      },
      onError: err => {
        setCustomerData(prev => {
          return {
            ...prev,
            isError: true,
            isLoading: false,
            isCreating: false,
            isCreateError: false
          }
        })
        onError && onError(err)
      }
    })
  }

  const _checkCustomerByID = ({ body, onStart, onSuccess, onError }) => {
    props.checkCustomerByID({
      body,
      onStart: () => {
        setCustomerData(prev => ({
          ...prev,
          isError: false,
          isLoading: true,
          isCreating: false,
          isCreateError: false
        }))
        onStart && onStart()
      },
      onSuccess: id => {
        setCustomerData(prev => ({
          ...prev,
          isLoading: false,
          isCreating: false,
          isError: id === null,
          isCreateError: false
        }))
        onSuccess && onSuccess()
      },
      onError: () => {
        setCustomerData(prev => ({
          ...prev,
          isError: true,
          isLoading: false,
          isCreating: false,
          isCreateError: false
        }))
        onError && onError()
      }
    })
  }

  const _validateMember = ({ body, onStart, onSuccess, onError }) => {
    props.validateMember({
      body,
      onStart: () => {
        setGroupData(prev => {
          // RETRY
          if (body.retry) {
            return {
              ...prev,
              members: prev.members.map(member => {
                if (member.number === body.number) {
                  return { ...member, error: null, status: STATUS.loading }
                } else {
                  return { ...member }
                }
              })
            }
          }
          // NEW NUMBER
          else {
            return {
              ...prev,
              members: [
                ...prev.members,
                {
                  role: ROLE.member,
                  number: body.number,
                  isGracePeriod: false,
                  status: STATUS.loading
                }
              ]
            }
          }
        })
        onStart && onStart()
      },
      onSuccess: () => {
        setGroupData(prev => {
          return {
            ...prev,
            members: prev.members.map(member => {
              if (member.number === body.number) {
                return { ...member, error: null, status: STATUS.temporary }
              } else {
                return { ...member }
              }
            })
          }
        })
        onSuccess && onSuccess()
      },
      onError: error => {
        setGroupData(prev => {
          return {
            ...prev,
            members: prev.members.map(member => {
              if (member.number === body.number) {
                return { ...member, error, status: STATUS.error }
              } else {
                return { ...member }
              }
            })
          }
        })
        onError && onError()
      }
    })
  }

  const handleBundleTypeChange = type => {
    if (type === null || Object.keys(BUNDLE_TYPE).includes(type)) {
      setBundleType(type)
    }
  }

  const handleCustomBundleUpdate = data =>
    setCustomBundleOptions(prev => ({ ...prev, ...data }))

  const resetCoverageFlag = () =>
    setLocationData(prev => ({ ...prev, hasCoverage: null }))

  const resetCoverageError = () =>
    setLocationData(prev => ({ ...prev, isError: false }))

  const resetLocationState = () => {
    setLocationData({
      street: '',
      country: '',
      city: 'Tirana',
      latitude: '',
      longitude: '',
      isError: false,
      isLoading: false,
      hasCoverage: null
    })
  }

  const handleIdDataUpdate = ({ field, value }) =>
    setIdData(prev => ({ ...prev, [field]: value }))

  const changeGroupName = name => setGroupData(prev => ({ ...prev, name }))

  const handleMemberRemove = ({ number }) => {
    setGroupData(prev => {
      return {
        ...prev,
        members: prev.members.filter(member => member.number !== number)
      }
    })
  }

  const resetCustomerCheckError = () =>
    setCustomerData(prev => ({ ...prev, isError: false }))

  const handleInstallDataUpdate = ({ installDate, installTime }) =>
    setInstallationData({ date: installDate, time: installTime })

  const updateCity = city => {
    setLocationData(prev => ({
      ...prev,
      city: city
    }))
  }

  return (
    <UnlimitedContext.Provider value={context}>
      <StatusBar backgroundColor={'transparent'} />
      <SafeAreaProvider>
        <UnlimitedHomeJourney
          // General
          onClose={props.onClose}
          // Bundle
          getATLBundles={_getATLBundles}
          selectBundle={setSelectedBundle}
          getPegaBundles={_getPegaBundles}
          changeBundleType={handleBundleTypeChange}
          // Custom Bundle
          getFixAndTVs={_getFixAndTVs}
          getTariffPlans={_getTariffPlans}
          getMemberSlots={_getMemberSlots}
          getCustomBundlePrice={_getCustomBundlePrice}
          updateCustomBundle={handleCustomBundleUpdate}
          // Map
          validateLocation={_validateLocation}
          resetCoverageFlag={resetCoverageFlag}
          resetLocationState={resetLocationState}
          resetCoverageError={resetCoverageError}
          getCoveredAreas={props.getCoveredAreas}
          checkCustomerByMSISDN={_checkCustomerByMSISDN}
          resetCustomerCheckError={resetCustomerCheckError}
          // Id
          createCustomer={_createCustomer}
          validateName={props.validateName}
          validateEmail={props.validateEmail}
          changeUserData={handleIdDataUpdate}
          checkCustomerByID={_checkCustomerByID}
          validatePersonalNumber={props.validatePersonalNumber}
          // Member
          validateMember={_validateMember}
          changeGroupName={changeGroupName}
          removeMember={handleMemberRemove}
          formatPhoneNumber={props.formatPhoneNumber}
          validateGroupName={props.validateGroupName}
          validatePhoneNumber={props.validatePhoneNumber}
          // Installation
          changeInstallData={handleInstallDataUpdate}
          // Summary
          onCheckout={props.onCheckout}
          // Web
          updateCity={updateCity}
        />
      </SafeAreaProvider>
    </UnlimitedContext.Provider>
  )
}

JourneyScreen.propTypes = {
  // General
  onClose: PropTypes.func,
  language: PropTypes.string,
  mapAPIKey: PropTypes.string,
  hasInstallation: PropTypes.bool,
  loggedInNumber: PropTypes.string,
  // Bundle
  getATLBundles: PropTypes.func,
  getPegaBundles: PropTypes.func,
  // Custom Bundle
  getFixAndTVs: PropTypes.func,
  getTariffPlans: PropTypes.func,
  getMemberSlots: PropTypes.func,
  updateCustomBundle: PropTypes.func,
  getCustomBundlePrice: PropTypes.func,
  // Location
  getCoveredAreas: PropTypes.func,
  validateLocation: PropTypes.func,
  checkCustomerByMSISDN: PropTypes.func,
  // Id
  validateName: PropTypes.func,
  validateEmail: PropTypes.func,
  createCustomer: PropTypes.func,
  changeUserData: PropTypes.func,
  checkCustomerByID: PropTypes.func,
  validatePersonalNumber: PropTypes.func,
  // Members
  validateMember: PropTypes.func,
  formatPhoneNumber: PropTypes.func,
  validateGroupName: PropTypes.func,
  validatePhoneNumber: PropTypes.func,
  // Installation
  installCalendar: PropTypes.object,
  // Summary
  onCheckout: PropTypes.func
}

JourneyScreen.defaultProps = {
  // General
  language: '',
  mapAPIKey: '',
  onClose: () => {},
  loggedInNumber: '',
  hasInstallation: false,
  // Bundle
  getATLBundles: () => {},
  getPegaBundles: () => {},
  // Custom Bundle
  getFixAndTVs: () => {},
  getTariffPlans: () => {},
  getMemberSlots: () => {},
  updateCustomBundle: () => {},
  getCustomBundlePrice: () => {},
  // Location
  validateLocation: () => {},
  getCoveredAreas: () => {},
  // Customer
  checkCustomerByMSISDN: () => {},
  // Id
  validateName: () => {},
  validateEmail: () => {},
  createCustomer: () => {},
  changeUserData: () => {},
  checkCustomerByID: () => {},
  validatePersonalNumber: () => {},
  // Members
  validateMember: () => {},
  formatPhoneNumber: () => {},
  validateGroupName: () => {},
  validatePhoneNumber: () => {},
  // Installation
  installCalendar: {},
  // Summary
  onCheckout: () => {}
}

export default withTheme(JourneyScreen)
